import type { CardData } from "../../constants/GlobalTypesAndEnumsAndConstants";

interface DeckCardsResponse {
  httpStatus: number;
  cfRayId?: string;
  responseData: {
    status: string;
    all_cards_for_deck: CardData[];
    all_cards_for_deck_and_subdecks: CardData[];
    resolved_deck_id?: string;
  };
}

// Share only pending network work. Completed responses must not survive an
// edit, deletion, generation update, or account change as a stale cache.
const pendingRequests = new Map<string, Promise<DeckCardsResponse>>();
let mutationVersion = 0;
const deckMutationVersions = new Map<string, number>();

export const getDeckCardsMutationVersion = (deckId?: string, relatedDeckIds: readonly string[] = []) =>
  JSON.stringify([mutationVersion, ...[...new Set([deckId ?? "", ...relatedDeckIds])].sort()
    .map(id => [id, deckMutationVersions.get(id) ?? 0])]);

export function invalidateDeckCardRequests(deckId?: string): void {
  if (!deckId) {
    mutationVersion += 1;
    pendingRequests.clear();
    return;
  }
  deckMutationVersions.set(deckId, (deckMutationVersions.get(deckId) ?? 0) + 1);
  for (const key of pendingRequests.keys()) {
    if (JSON.parse(key)[2] === deckId) pendingRequests.delete(key);
  }
}

export function cardUpdateChangesContent(fields: Record<string, unknown>): boolean {
  // Learner ratings and review scheduling arrive through the cards listener;
  // they must not invalidate content and start another complete deck read.
  return Object.keys(fields).some(field => {
    const isLearnerRating = field === "knowledge_understanding_ratings";
    const isReviewSchedule = field.startsWith("frfs_learning_step_by_user_") ||
      field.startsWith("frfs_next_review_at_by_user_");
    return !isLearnerRating && !isReviewSchedule;
  });
}

export function requestDeckCards(endpoint: string, deckId: string, userId?: string, cardSetRevision?: string) {
  const requestKey = JSON.stringify([endpoint, userId ?? null, deckId, cardSetRevision ?? null]);
  // A caller without a publication revision cannot prove that an older
  // pending query contains the card set it now needs. Always fetch it fresh.
  const canCoalesce = cardSetRevision !== undefined;
  let request = canCoalesce ? pendingRequests.get(requestKey) : undefined;
  const requestAttempts = request ? 0 : 1;
  if (!request) {
    request = (async () => {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, deck_id: deckId }),
        });
        return {
          httpStatus: response.status,
          cfRayId: response.headers?.get("cf-ray") || undefined,
          responseData: await response.json(),
        };
      } finally {
        if (pendingRequests.get(requestKey) === request) pendingRequests.delete(requestKey);
      }
    })();
    if (canCoalesce) pendingRequests.set(requestKey, request);
  }
  // Callers mutate answers and ratings while preparing a study session. Each
  // caller needs its own objects even when the network response is shared.
  // The body is parsed JSON; copy it without requiring structuredClone in older WebViews.
  return {
    request: request.then((response) => ({
      ...response,
      responseData: JSON.parse(JSON.stringify(response.responseData)),
    })),
    requestAttempts,
  };
}
